/**
 * Statut Déploiement — Dashboard de santé Kaizen.
 * Route admin : /app/deploiement
 * Affiche état de tous les modules, bloquants, warnings → décision go/no-go déploiement.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Rocket } from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  OK: <CheckCircle2 className="w-5 h-5 text-emerald-400" />,
  WARNING: <AlertTriangle className="w-5 h-5 text-amber-400" />,
  FAIL: <XCircle className="w-5 h-5 text-rose-400" />,
  READY: <CheckCircle2 className="w-5 h-5 text-emerald-400" />,
  AWAITING_CHANNEL: <AlertTriangle className="w-5 h-5 text-amber-400" />,
};

export default function DeploymentStatusModule() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ytHandle, setYtHandle] = useState("");
  const [ytLoading, setYtLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/deployment/audit");
      setAudit(r.data);
    } catch { toast.error("Erreur audit"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setYouTubeChannel = async () => {
    if (!ytHandle.trim()) return toast.error("Donne ton handle (@KruRalph) ou URL chaîne");
    setYtLoading(true);
    try {
      const payload = ytHandle.startsWith("http")
        ? { channel_url: ytHandle.trim() }
        : { handle: ytHandle.replace("@", "").trim() };
      const r = await api.post("/youtube/channel/set", payload);
      toast.success(`YouTube configuré : ${r.data.channel_id}`);
      await api.post("/youtube/channel/sync");
      toast.success("Sync initial lancé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setYtLoading(false);
  };

  if (loading) return <div className="p-6"><Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto" /></div>;
  if (!audit) return <div className="p-6 text-rose-400">Erreur chargement audit</div>;

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto" data-testid="deployment-module">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-amber-100 tracking-tight flex items-center gap-2">
            <Rocket className="w-7 h-7 text-amber-400" /> Statut déploiement
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Audit global de tous les modules Kaizen — décision go/no-go déploiement.
          </p>
        </div>
        <button onClick={load} className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs flex items-center gap-1" data-testid="refresh-btn">
          <RefreshCw className="w-3 h-3" /> Rafraîchir
        </button>
      </div>

      {/* VERDICT */}
      <div className={`rounded-2xl border-2 p-6 ${audit.ready_for_deployment
          ? "border-emerald-500/50 bg-gradient-to-br from-emerald-500/10 to-zinc-950"
          : "border-rose-500/50 bg-gradient-to-br from-rose-500/10 to-zinc-950"}`}
        data-testid="verdict">
        <div className="flex items-center gap-3">
          {audit.ready_for_deployment ? <CheckCircle2 className="w-10 h-10 text-emerald-400" /> : <XCircle className="w-10 h-10 text-rose-400" />}
          <div>
            <div className="text-2xl font-bold text-amber-100">{audit.summary}</div>
            <div className="text-sm text-zinc-400 mt-1">
              {audit.blockers_count} bloquant(s) · {audit.warnings_count} warning(s) · {Object.keys(audit.modules).length} modules audités
            </div>
          </div>
        </div>
      </div>

      {/* BLOCKERS */}
      {audit.blockers_count > 0 && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-5">
          <h3 className="text-md font-semibold text-rose-200 mb-3">🚫 Bloquants déploiement</h3>
          <ul className="space-y-1 text-sm text-rose-100">
            {audit.blockers.map((b, i) => <li key={i} data-testid={`blocker-${i}`}>• {b}</li>)}
          </ul>
        </div>
      )}

      {/* WARNINGS */}
      {audit.warnings_count > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
          <h3 className="text-md font-semibold text-amber-200 mb-3">⚠️ Warnings (non-bloquants)</h3>
          <ul className="space-y-1 text-sm text-amber-100">
            {audit.warnings.map((w, i) => <li key={i} data-testid={`warning-${i}`}>• {w}</li>)}
          </ul>
        </div>
      )}

      {/* YouTube setup */}
      {!audit.modules.youtube?.channel_configured && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5">
          <h3 className="text-md font-semibold text-red-200 mb-2">📺 Configure ta chaîne YouTube (1 fois)</h3>
          <p className="text-xs text-red-100/70 mb-3">
            Une fois configuré, le système synchronise <strong>automatiquement</strong> tes nouvelles vidéos toutes les heures.
            Tu n'as plus à les annoncer.
          </p>
          <div className="flex gap-2 max-w-md">
            <input placeholder="@KruRalph ou URL complète" value={ytHandle}
              onChange={(e) => setYtHandle(e.target.value)}
              className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-amber-100"
              data-testid="yt-handle-input" />
            <button onClick={setYouTubeChannel} disabled={ytLoading}
              className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-zinc-950 font-semibold text-xs"
              data-testid="yt-set-btn">
              {ytLoading ? "..." : "Configurer + sync"}
            </button>
          </div>
        </div>
      )}

      {/* MODULES GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(audit.modules).map(([key, mod]) => (
          <div key={key} className="rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid={`module-${key}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-amber-100 capitalize">{key.replace(/_/g, " ")}</h4>
              {ICONS[mod.status] || <span className="text-zinc-500 text-xs">{mod.status}</span>}
            </div>
            <div className="space-y-1 text-xs text-zinc-300">
              {Object.entries(mod).filter(([k]) => k !== "status" && k !== "note").map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2">
                  <span className="text-zinc-500 truncate">{k.replace(/_/g, " ")}</span>
                  <span className={typeof v === "boolean" ? (v ? "text-emerald-300" : "text-rose-300") : "text-amber-200"}>
                    {Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "✓" : "✗") : String(v)}
                  </span>
                </div>
              ))}
              {mod.note && <div className="text-zinc-500 italic mt-2 text-[10px]">{mod.note}</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="text-[10px] text-zinc-600 text-right">
        Audit généré : {new Date(audit.timestamp).toLocaleString("fr-CA")}
      </div>
    </div>
  );
}
